// Centralized event logger for analytics

import axios from "axios";

// Utility to get OS and Browser information
function getOSAndBrowser() {
    let os = "Unknown OS";
    let browser = "Unknown Browser";
    const ua = navigator.userAgent;

    // OS detection
    if (/Windows NT 10/.test(ua)) {
        if (/Windows NT 10\.0;\s*Win64;/.test(ua) && /Windows 11/.test(ua)) {
            os = "Windows 11";
        } else if (/Windows NT 10\.0;\s*Win64;/.test(ua)) {
            os = "Windows 11";
        } else {
            os = "Windows 10";
        }
    }
    else if (/Windows NT 6.3/.test(ua)) os = "Windows 8.1";
    else if (/Windows NT 6.2/.test(ua)) os = "Windows 8";
    else if (/Windows NT 6.1/.test(ua)) os = "Windows 7";
    else if (/Mac OS X/.test(ua)) os = "Mac OS X";
    else if (/Android/.test(ua)) os = "Android";
    else if (/Linux/.test(ua)) os = "Linux";
    else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";

    // Browser detection
    if (/Edg\//.test(ua)) browser = "Edge";
    else if (/Chrome\//.test(ua)) browser = "Chrome";
    else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";
    else if (/Firefox\//.test(ua)) browser = "Firefox";
    else if (/MSIE|Trident/.test(ua)) browser = "Internet Explorer";

    return { os, browser };
}

// Utility to get screen resolution
function getScreenResolution() {
    if (typeof window !== "undefined" && window.screen) {
        return `${window.screen.width}x${window.screen.height}`;
    }
    return "unknown";
}

// Utility to sanitize sensitive fields and remove emojis/unicode from metadata
function removeEmojisAndNonAlphanumeric(str) {
    // Remove emojis (most common ranges) and non-alphanumeric except space
    // Explicitly include 💾 (U+1F4BE) in the regex
    return str
        .replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2B50}-\u{2B55}\u{1F4BE}]/gu, "")
        .replace(/[^a-zA-Z0-9 ]+/g, "");
}

function sanitizeMetadata(meta) {
    if (typeof meta !== "object" || meta === null) return meta;
    const filtered = { ...meta };
    ["fname", "first_name", "email", "password", "confirm_password"].forEach(key => {
        if (key in filtered) filtered[key] = undefined;
    });
    Object.keys(filtered).forEach(key => {
        if (typeof filtered[key] === "string") {
            filtered[key] = removeEmojisAndNonAlphanumeric(filtered[key]);
        } else if (typeof filtered[key] === "object" && filtered[key] !== null) {
            ["fname", "first_name", "email", "password", "confirm_password"].forEach(skey => {
                if (skey in filtered[key]) filtered[key][skey] = undefined;
            });
            Object.keys(filtered[key]).forEach(subkey => {
                if (typeof filtered[key][subkey] === "string") {
                    filtered[key][subkey] = removeEmojisAndNonAlphanumeric(filtered[key][subkey]);
                }
            });
        }
    });
    return filtered;
}

// Utility to get device timezone
function getDeviceTimezone() {
    if (typeof Intl !== "undefined" && Intl.DateTimeFormat) {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown";
    }
    return "unknown";
}

// Read account context from session (global for all logged-in members)
function getAccountContext() {
    try {
        const account_type = sessionStorage.getItem("account_type") || undefined;
        const account_status = sessionStorage.getItem("account_status") || undefined;
        const ton_version = sessionStorage.getItem("TonVersion") || undefined;
        return { account_type, account_status, ton_version };
    } catch {
        return { account_type: undefined, account_status: undefined, ton_version: undefined };
    }
}

export function logEvent({
    event_type,
    user_id,
    module_id,
    module_name,
    subcomponent,
    screen,
    device_is_desktop,
    is_click,
    clicks,
    unique_clicks,
    metadata = {},
    schema_version = 1,
    os,
}) {
    const browserInfo = getOSAndBrowser();
    const screen_resolution = getScreenResolution();
    const device_timezone = getDeviceTimezone();
    const { account_type, account_status, ton_version } = getAccountContext();
    const safeMetadata = sanitizeMetadata(metadata);
    const eventObj = {
        timestamp: new Date().toISOString(),
        event_type,
        user_id,
        module_id,
        module_name,
        subcomponent,
        screen,
        device_is_desktop,
        is_click,
        clicks,
        unique_clicks,
        metadata: safeMetadata,
        schema_version,
        os: os || browserInfo.os,
        browser: browserInfo.browser,
        screen_resolution,
        device_timezone,
        account_type,
        account_status,
        buildnum: ton_version, // Pass TonVersion as buildnum
    };

    // Send to analytics backend
    axios.post(
        `${process.env.REACT_APP_API_URL}/api/analytics`,
        {
            event_type,
            user_id,
            module_id,
            module_name,
            subcomponent: eventObj.subcomponent,
            screen,
            device_is_desktop,
            is_click,
            clicks,
            unique_clicks,
            browser: browserInfo.browser,
            operating_system: os || browserInfo.os,
            screen_resolution,
            device_timezone: device_timezone,
            metadata: typeof safeMetadata === "object" ? JSON.stringify(safeMetadata) : safeMetadata,
            metadata_action: safeMetadata.metadata_action,
            button_link_value: safeMetadata.button_link_value,
            account_type: eventObj.account_type,
            account_status: eventObj.account_status,
            buildnum: eventObj.buildnum, // Pass TonVersion as buildnum
        },
        { withCredentials: true }
    ).catch(() => { /* Optionally handle error */ });

    return eventObj;
}

// Utility to parse module_id and module_name from filename
export function getModuleInfoFromFilename() {
    let filename = "";
    if (typeof import.meta !== "undefined" && import.meta.url) {
        filename = import.meta.url.split("/").pop();
    } else {
        filename = "_7_daily_log_main.js";
    }
    const idMatch = filename.match(/^_([\d_]+)_/);
    const module_id = idMatch ? idMatch[1].replace(/_/g, "") : "";
    const module_name = filename.replace(/\.js$/, "");
    return { module_id, module_name };
}

export function handleUIEvent(e, metadata = {}, setLastEvent, screen, module_name, subcomponent) {
    const tagName = e?.currentTarget?.tagName?.toLowerCase() || "custom";

    // Only capture events for allowed elements
    const allowedTags = ["button", "a", "select", "option", "input", "form"];
    let isAllowedInput = false;
    if (tagName === "input") {
        const type = e?.currentTarget?.type;
        isAllowedInput = type === "radio" || type === "checkbox";
    }

    if (
        !allowedTags.includes(tagName) ||
        (tagName === "input" && !isAllowedInput) ||
        tagName === "input"
    ) {
        // Do not log events for text inputs or other disallowed elements
        return;
    }

    let event_type = "";
    if (tagName === "button") event_type = "button_click";
    else if (tagName === "a") event_type = "link_click";
    else if (tagName === "form") event_type = "form_submit";
    else if (tagName === "select" || tagName === "option") event_type = "dropdown_change";
    else if (tagName === "input" && isAllowedInput) event_type = `${e.currentTarget.type}_change`;
    else event_type = `${tagName}_event`;

    // Remove .js extension if present
    let cleanModuleName = module_name;
    if (typeof cleanModuleName === "string" && cleanModuleName.endsWith(".js")) {
        cleanModuleName = cleanModuleName.replace(/\.js$/, "");
    }

    // Remove sensitive fields and values from metadata
    const filteredMetadata = sanitizeMetadata({ ...metadata });
    delete filteredMetadata.password;
    delete filteredMetadata.confirm_password;
    delete filteredMetadata.email;

    if (
        (filteredMetadata.field === "email" ||
            filteredMetadata.field === "password" ||
            filteredMetadata.field === "confirm_password")
        && "value" in filteredMetadata
    ) {
        filteredMetadata.value = undefined;
    }

    // Capture button or link value
    let button_link_value = undefined;
    if (e?.currentTarget) {
        if (tagName === "button") {
            button_link_value =
                e.currentTarget.value ||
                e.currentTarget.innerText ||
                e.currentTarget.textContent ||
                undefined;
        } else if (tagName === "a") {
            button_link_value =
                e.currentTarget.innerText ||
                e.currentTarget.textContent ||
                e.currentTarget.href ||
                undefined;
        } else if (tagName === "select" || tagName === "option") {
            button_link_value = e.currentTarget.value;
        } else if (tagName === "input" && isAllowedInput) {
            button_link_value = e.currentTarget.value;
        }
    }
    if (button_link_value !== undefined) {
        button_link_value = removeEmojisAndNonAlphanumeric(String(button_link_value));
        filteredMetadata.button_link_value = button_link_value;
    }

    const { os, browser } = getOSAndBrowser();
    const screen_resolution = getScreenResolution();
    const device_timezone = getDeviceTimezone();
    const { account_type, account_status, ton_version } = getAccountContext();

    // Prefer passed user_id, fallback to sessionStorage, fallback to 1
    let resolvedUserId = undefined;
    if (typeof setLastEvent === "number") {
        resolvedUserId = setLastEvent;
    } else if (typeof setLastEvent === "string") {
        resolvedUserId = Number(setLastEvent);
    } else if (typeof setLastEvent === "function") {
        resolvedUserId = setLastEvent();
    } else {
        const sessionUserId = sessionStorage.getItem("user_id");
        resolvedUserId = sessionUserId !== null ? Number(sessionUserId) : 1;
    }

    const eventObj = {
        event_type,
        user_id: resolvedUserId,
        module_name: cleanModuleName,
        subcomponent,
        screen,
        device_is_desktop: (window.innerWidth >= 1024 && window.innerHeight >= 768) ? 1 : 0,
        is_click: 1,
        clicks: 1,
        unique_clicks: 1,
        metadata: filteredMetadata,
        os,
        browser,
        screen_resolution,
        device_timezone,
        account_type,
        account_status,
        buildnum: ton_version, // Pass TonVersion as buildnum
    };
    //console.log(eventObj);

    // Send to analytics backend
    axios.post(
        `${process.env.REACT_APP_API_URL}/api/analytics`,
        {
            event_type,
            user_id: eventObj.user_id,
            module_name: eventObj.module_name,
            subcomponent: eventObj.subcomponent,
            screen: eventObj.screen,
            device_is_desktop: eventObj.device_is_desktop,
            is_click: eventObj.is_click,
            clicks: eventObj.clicks,
            unique_clicks: eventObj.unique_clicks,
            browser: eventObj.browser,
            operating_system: eventObj.os,
            screen_resolution: eventObj.screen_resolution,
            device_timezone: eventObj.device_timezone,
            metadata: typeof eventObj.metadata === "object" ? JSON.stringify(eventObj.metadata) : eventObj.metadata,
            metadata_action: eventObj.metadata.metadata_action,
            button_link_value: eventObj.metadata.button_link_value,
            account_type: eventObj.account_type,
            account_status: eventObj.account_status,
            buildnum: eventObj.buildnum, // Pass TonVersion as buildnum
        },
        { withCredentials: true }
    ).catch(() => { /* Optionally handle error */ });
}

/**
 * Generic analytics event handler for UI interactions.
 * Attach this handler to dropdowns, links, checkboxes, accordions, etc.
 */
export function analyticsHandler({ screen, module_name, user_id, event_type: forcedEventType, subcomponent } = {}) {
    return function (e) {
        if (!e) return;

        // Determine tag and type
        const tagName = e.currentTarget?.tagName?.toLowerCase() || "custom";
        const inputType = e.currentTarget?.type?.toLowerCase();

        // Determine event type
        let event_type = forcedEventType;
        if (!event_type) {
            if (tagName === "select" || tagName === "option") event_type = "dropdown_change";
            else if (tagName === "a") event_type = "link_click";
            else if (tagName === "input" && (inputType === "checkbox" || inputType === "radio")) event_type = `${inputType}_change`;
            else if (tagName === "div" && e.currentTarget?.getAttribute("data-accordion") === "true") event_type = "accordion_expand";
            else event_type = `${tagName}_event`;
        }

        // Get button/link/accordion value or label
        let button_link_value;
        if (tagName === "button" || tagName === "a" || tagName === "div" || tagName === "span") {
            button_link_value =
                e.currentTarget.getAttribute("aria-label") ||
                e.currentTarget.innerText ||
                e.currentTarget.textContent ||
                e.currentTarget.value ||
                undefined;
        } else if (tagName === "select" || tagName === "option" || (tagName === "input" && (inputType === "checkbox" || inputType === "radio"))) {
            button_link_value = e.currentTarget.value;
        }

        // Sanitize value
        if (button_link_value !== undefined) {
            button_link_value = removeEmojisAndNonAlphanumeric(String(button_link_value));
        }

        // Compose metadata
        const metadata = {
            button_link_value,
            ...(e.detail ? { detail: e.detail } : {}),
        };

        // Prefer passed user_id, fallback to sessionStorage, fallback to 1
        let resolvedUserId = user_id;
        if (resolvedUserId === undefined || resolvedUserId === null) {
            const sessionUserId = sessionStorage.getItem("user_id");
            resolvedUserId = sessionUserId !== null ? Number(sessionUserId) : 1;
        }

        const browserInfo = getOSAndBrowser();
        const screen_resolution = getScreenResolution();
        const device_timezone = getDeviceTimezone();
        const { account_type, account_status, ton_version } = getAccountContext();

        const eventObj = {
            event_type,
            user_id: resolvedUserId,
            module_name,
            subcomponent,
            screen,
            device_is_desktop: (window.innerWidth >= 1024 && window.innerHeight >= 768) ? 1 : 0,
            is_click: 1,
            clicks: 1,
            unique_clicks: 1,
            metadata,
            os: browserInfo.os,
            browser: browserInfo.browser,
            screen_resolution,
            device_timezone,
            account_type,
            account_status,
            buildnum: ton_version, // Pass TonVersion as buildnum
        };

        // Send to analytics backend
        axios.post(
            `${process.env.REACT_APP_API_URL}/api/analytics`,
            {
                event_type: eventObj.event_type,
                user_id: eventObj.user_id,
                module_name: eventObj.module_name,
                subcomponent: eventObj.subcomponent,
                screen: eventObj.screen,
                device_is_desktop: eventObj.device_is_desktop,
                is_click: eventObj.is_click,
                clicks: eventObj.clicks,
                unique_clicks: eventObj.unique_clicks,
                browser: eventObj.browser,
                operating_system: eventObj.os,
                screen_resolution: eventObj.screen_resolution,
                device_timezone: eventObj.device_timezone,
                metadata: JSON.stringify(eventObj.metadata),
                button_link_value: eventObj.metadata.button_link_value,
                account_type: eventObj.account_type,
                account_status: eventObj.account_status,
                buildnum: eventObj.buildnum, // Pass TonVersion as buildnum
            },
            { withCredentials: true }
        ).catch(() => { /* Optionally handle error */ });
    };
}

// Global analytics listener for automatic tracking
export function globalAnalyticsListener(e) {
    const tag = e.target.tagName?.toLowerCase();
    const type = e.target.type?.toLowerCase();

    // Find the closest ancestor with data-analytics (including self)
    let moduleEl = e.target.closest("[data-analytics]");
    let module_name = moduleEl ? moduleEl.getAttribute("data-analytics") : undefined;

    // Use the id of the element as subcomponent if available
    let subcomponent = undefined;
    if ((["select", "input", "button", "a"].includes(tag)) && e.target.id) {
        subcomponent = e.target.id;
    }

    if (tag === "input" && (type === "checkbox" || type === "radio")) {
        analyticsHandler({ event_type: `${type}_change`, module_name, subcomponent })(e);
    }
    else if (tag === "button") {
        analyticsHandler({ event_type: "button_click", module_name, subcomponent })(e);
    }
    else if (tag === "a") {
        analyticsHandler({ event_type: "link_click", module_name, subcomponent })(e);
    }
    else if (tag === "select") {
        analyticsHandler({ event_type: "dropdown_change", module_name, subcomponent })(e);
    }
    // Add more as needed
}

